import React from "react";
import {
  FaCode,
  FaDollarSign,
  FaExternalLinkAlt,
  FaFileAlt,
  FaFileInvoiceDollar,
  FaUndo,
  FaUsers,
} from "react-icons/fa";
import { calculateDevCost, calculateLoanRunCost, WORK_HOURS_PER_YEAR } from "../calculators";
import { PILOT_STANDARD } from "../assumptions";
import { useClientCosts, useClientCostsList, useClientOverview } from "../ClientOverviewContext";
import {
  averageCostPerLoan,
  buildPilotCostPrefill,
  costWindowDays,
  PILOT_SPECIFIC_KEYS,
  PLAN_PILOT_ID,
  projectAtPace,
  resolveCostInputs,
} from "../costSelectors";
import {
  formatDate,
  formatMoney,
  formatNumber,
  formatPercent,
  hubspotDealUrl,
} from "../selectors";
import { Metric } from "./Metric";
import { NumberField } from "./NumberField";

const STANDARD_NOTE = `Team standard: ${PILOT_STANDARD.weeks} weeks, one FDE at ${PILOT_STANDARD.hoursPerWeek} h/week, ${PILOT_STANDARD.loanOfficers} LOs × ${PILOT_STANDARD.loansPerOfficer} loans.`;

function withoutKeys(object, keys) {
  return Object.fromEntries(Object.entries(object).filter(([key]) => !keys.includes(key)));
}

function costPerRunHint({ source, pilot, costs, average }) {
  if (source === "db") {
    return `Real cost per loan for ${costs.name}, last ${costs.days} days. It already includes re-runs.`;
  }
  if (source === "average") {
    const note = average.isPartial ? " Parsing cost is missing for some of them." : "";
    const pilotsLabel = average.pilots === 1 ? "1 running pilot" : `${average.pilots} running pilots`;

    return `Real average of ${pilotsLabel} (${formatNumber(average.loans)} loans).${note}`;
  }
  if (source === "edited") {
    return "Typed in.";
  }
  if (pilot && !pilot.organizationId) {
    return "This pilot is not linked to Addy yet and no running pilot has cost data. Type an estimate.";
  }

  return "No real cost data yet. Type an estimate.";
}

function PilotActuals({ pilot, costs, overview, plannedDays }) {
  const team = overview.data?.team;
  const data = costs.data;
  const pace = projectAtPace({ costs: data, daysElapsed: pilot.daysInPilot, plannedDays });
  const facts = [
    pilot.pilotStartedAt ? `Started ${formatDate(pilot.pilotStartedAt)}` : "Start date not set",
    pilot.daysInPilot !== null && pilot.daysInPilot !== undefined
      ? `day ${formatNumber(pilot.daysInPilot)}`
      : null,
    pilot.fde ? `FDE ${pilot.fde}` : "No FDE in HubSpot",
    pilot.owner ? `owner ${pilot.owner}` : null,
  ].filter(Boolean);

  return (
    <section className="pl-card pl-card--pad pl-actuals" aria-label="Actual so far">
      <header className="pl-actuals-head">
        <div>
          <h3>Actual so far</h3>
          <p className="pl-muted">From HubSpot and Addy's database · {facts.join(" · ")}</p>
        </div>
        <a className="pl-btn" href={hubspotDealUrl(pilot.dealId)} target="_blank" rel="noreferrer">
          <FaExternalLinkAlt aria-hidden="true" /> Open deal in HubSpot
        </a>
      </header>

      {!pilot.organizationId ? (
        <p className="pl-note">
          This pilot is not linked to an Addy organization yet (no deal contact is a member of
          one), so there is no real usage or cost to show.
        </p>
      ) : null}

      {pilot.organizationId && costs.error ? (
        <div className="pl-banner pl-banner--error" role="alert">
          <strong>{costs.error}</strong>
          <button type="button" className="pl-btn" onClick={costs.retry}>
            Try again
          </button>
        </div>
      ) : null}

      {pilot.organizationId && !data && costs.loading ? (
        <p className="pl-muted">Loading real costs from Addy's database…</p>
      ) : null}

      {data ? (
        <>
          <div className="pl-metrics pl-metrics--four">
            <Metric
              icon={FaUsers}
              label="People"
              value={team ? `${formatNumber(team.accepted)} accepted` : "—"}
              hint={
                team
                  ? `of ${formatNumber(team.invited)} invited · ${formatNumber(team.active)} active in 14 days`
                  : overview.error || "Loading…"
              }
              tone="violet"
            />
            <Metric
              icon={FaFileAlt}
              label="Loans processed"
              value={formatNumber(data.loansProcessed)}
              hint={`Last ${formatNumber(data.days)} days`}
            />
            <Metric
              icon={FaDollarSign}
              label="AI cost"
              value={formatMoney(data.totalUsd, { cents: true })}
              hint={
                data.extraction
                  ? `${formatMoney(data.llm.totalUsd, { cents: true })} LLM + ${formatMoney(
                      data.extraction.totalUsd,
                      { cents: true },
                    )} parsing`
                  : `${formatMoney(data.llm.totalUsd, { cents: true })} LLM · parsing cost unavailable`
              }
              tone="green"
            />
            <Metric
              icon={FaDollarSign}
              label="Cost per loan"
              value={
                data.costPerLoanUsd === null ? "—" : formatMoney(data.costPerLoanUsd, { cents: true })
              }
              hint={data.costPerLoanUsd === null ? "No loans processed yet" : "AI cost ÷ loans processed"}
              tone="amber"
            />
          </div>

          {pace ? (
            <p className="pl-note">
              At this pace the full {formatNumber(plannedDays / 7)}-week pilot comes to about{" "}
              <strong>{formatNumber(pace.loans)} loans</strong> and{" "}
              <strong>{formatMoney(pace.totalUsd, { cents: true })}</strong> of AI cost.
            </p>
          ) : null}

          {data.byTask.length ? (
            <details className="pl-chart-table">
              <summary>Where the AI cost went</summary>
              <div className="pl-table-wrap">
                <table className="pl-table">
                  <thead>
                    <tr>
                      <th className="pl-left">Task</th>
                      <th>Source</th>
                      <th>Runs</th>
                      <th>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.byTask.map((task) => (
                      <tr key={task.key}>
                        <td className="pl-left">{task.label}</td>
                        <td>{task.source === "llm" ? "LLM" : "Parsing"}</td>
                        <td>{formatNumber(task.runs)}</td>
                        <td>{formatMoney(task.totalUsd, { cents: true })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          ) : null}

          <p className="pl-field-hint">
            LLM cost covers the traced pipelines (document classification and loan task runs)
            {data.llmSealedThrough ? `, rolled up through ${formatDate(data.llmSealedThrough)}` : ""}.
            Other LLM calls are not tracked per client yet.
            {pilot.daysInPilot > data.days
              ? ` Costs are kept 35 days, so this is the last ${data.days}.`
              : ""}
            {data.isTruncated ? " Too many records to read them all, so these totals are low." : ""}
          </p>
        </>
      ) : null}
    </section>
  );
}

/**
 * What a pilot costs us: engineering time plus the cost of running Addy on
 * the pilot's loans. A running pilot fills in from HubSpot and Addy's
 * database; anything typed in wins until reset.
 */
export function PilotCostCalculator({ pilots, pilotsLoading, state, onChange }) {
  const pilot = pilots.find((entry) => entry.dealId === state.pilotId) || null;
  const organizationId = pilot?.organizationId || null;
  const costs = useClientCosts(organizationId, costWindowDays(pilot));
  const overview = useClientOverview(organizationId);
  const averageEntries = useClientCostsList(
    pilots
      .filter((entry) => entry.organizationId)
      .map((entry) => ({ organizationId: entry.organizationId, days: costWindowDays(entry) })),
  );
  const average = averageCostPerLoan(averageEntries);
  const prefill = buildPilotCostPrefill({
    pilot,
    costs: costs.data,
    team: overview.data?.team,
    average,
  });
  const { values, sources } = resolveCostInputs({ prefill, edits: state.edits });

  const dev = calculateDevCost(values);
  const loans = calculateLoanRunCost(values);
  const totalCost = dev.totalCost + loans.totalCost;
  const field = (key) => ({
    id: `pl-cost-${key}`,
    value: values[key],
    source: sources[key],
    onChange: (value) => onChange({ ...state, edits: { ...state.edits, [key]: value } }),
  });
  const selectPilot = (pilotId) =>
    onChange({ pilotId, edits: withoutKeys(state.edits, PILOT_SPECIFIC_KEYS) });
  const loansSoFar =
    costs.data && overview.data?.team?.accepted
      ? costs.data.loansProcessed / overview.data.team.accepted
      : null;

  return (
    <div className="pl-calc">
      <section className="pl-card pl-card--pad">
        <div className="pl-setup">
          <div className="pl-field">
            <label className="pl-field-label" htmlFor="pl-cost-pilot">
              Pilot
            </label>
            <select
              id="pl-cost-pilot"
              className="pl-select"
              value={pilot ? pilot.dealId : PLAN_PILOT_ID}
              onChange={(event) => selectPilot(event.target.value)}
            >
              <option value={PLAN_PILOT_ID}>Plan a new pilot</option>
              {pilots.map((entry) => (
                <option key={entry.dealId} value={entry.dealId}>
                  {entry.name}
                  {entry.organizationId ? "" : " (not linked to Addy)"}
                </option>
              ))}
            </select>
            <span className="pl-field-hint">
              {pilotsLoading && !pilots.length
                ? "Loading running pilots from HubSpot…"
                : "Running pilots from HubSpot. Pick one to use its real numbers."}
            </span>
          </div>
          <NumberField {...field("weeks")} label="Pilot length" suffix="weeks" />
          <button
            type="button"
            className="pl-btn pl-btn--ghost"
            onClick={() => onChange({ ...state, edits: {} })}
            disabled={!Object.keys(state.edits).length}
          >
            <FaUndo aria-hidden="true" /> Back to real numbers
          </button>
        </div>
        <p className="pl-note">{STANDARD_NOTE}</p>
      </section>

      {pilot ? (
        <PilotActuals
          pilot={pilot}
          costs={costs}
          overview={overview}
          plannedDays={(Number(values.weeks) || PILOT_STANDARD.weeks) * 7}
        />
      ) : null}

      <div className="pl-calc-grid">
        <section className="pl-card pl-card--pad">
          <header className="pl-calc-head">
            <span className="pl-calc-icon">
              <FaCode aria-hidden="true" />
            </span>
            <div>
              <h3>Developer cost</h3>
              <p>Engineering time we put into the pilot.</p>
            </div>
          </header>
          <div className="pl-fields">
            <NumberField
              {...field("annualSalary")}
              label="Developer cost per year"
              prefix="$"
              step={1000}
            />
            <NumberField
              {...field("overheadPct")}
              label="Taxes & benefits on top"
              suffix="%"
              hint="Leave at 0 to use the salary as is."
            />
            <NumberField {...field("hoursPerWeek")} label="Hours per week on the pilot" suffix="h" />
            <NumberField
              {...field("developers")}
              label="Developers"
              hint={
                sources.developers === "hubspot"
                  ? pilot.fde
                    ? `FDE in HubSpot: ${pilot.fde}.`
                    : "No FDE set on the deal in HubSpot."
                  : null
              }
            />
          </div>
          <div className="pl-result">
            <span className="pl-result-label">Developer cost for the pilot</span>
            <span className="pl-result-value">{formatMoney(dev.totalCost)}</span>
            <span className="pl-formula">
              {formatMoney(dev.hourlyRate, { cents: true })}/h ×{" "}
              {formatNumber(Number(values.hoursPerWeek) || 0, 1)} h/week ×{" "}
              {formatNumber(Number(values.weeks) || 0, 1)} weeks ×{" "}
              {formatNumber(Number(values.developers) || 0, 1)} dev ={" "}
              {formatNumber(dev.totalHours, 1)} h
            </span>
            <span className="pl-result-hint">
              Hourly rate = yearly cost ÷ {formatNumber(WORK_HOURS_PER_YEAR)} work hours. That is{" "}
              {formatPercent(dev.shareOfFullTime)} of a full-time week,{" "}
              {formatMoney(dev.weeklyCost)} per week.
            </span>
          </div>
        </section>

        <section className="pl-card pl-card--pad">
          <header className="pl-calc-head">
            <span className="pl-calc-icon pl-calc-icon--green">
              <FaFileInvoiceDollar aria-hidden="true" />
            </span>
            <div>
              <h3>Loan run cost</h3>
              <p>What running Addy on the pilot's loans costs us.</p>
            </div>
          </header>
          <div className="pl-fields">
            <NumberField
              {...field("costPerRun")}
              label="AI cost per loan run"
              prefix="$"
              step={0.01}
              placeholder="No data"
              hint={costPerRunHint({ source: sources.costPerRun, pilot, costs: costs.data, average })}
            />
            <NumberField
              {...field("loanOfficers")}
              label="Loan officers in the pilot"
              hint={
                sources.loanOfficers === "db"
                  ? "People who accepted their Addy invite (Addy staff left out)."
                  : null
              }
            />
            <NumberField
              {...field("loansPerOfficer")}
              label="Loans per loan officer"
              hint={
                loansSoFar === null
                  ? "Over the whole pilot."
                  : `Over the whole pilot. So far: ${formatNumber(loansSoFar, 1)} per person (Addy database).`
              }
            />
            <NumberField
              {...field("runsPerLoan")}
              label="Runs per loan"
              step={0.5}
              hint={
                sources.runsPerLoan === "db"
                  ? "1, because the real cost per loan already includes re-runs."
                  : "Re-runs when new documents arrive."
              }
            />
          </div>
          <div className="pl-result">
            <span className="pl-result-label">Loan run cost for the pilot</span>
            <span className="pl-result-value">{formatMoney(loans.totalCost, { cents: true })}</span>
            <span className="pl-formula">
              {formatNumber(Number(values.loanOfficers) || 0, 1)} LOs ×{" "}
              {formatNumber(Number(values.loansPerOfficer) || 0, 1)} loans ×{" "}
              {formatNumber(Number(values.runsPerLoan) || 0, 1)} runs ×{" "}
              {formatMoney(Number(values.costPerRun) || 0, { cents: true })} ={" "}
              {formatNumber(loans.totalRuns, 1)} runs
            </span>
            <span className="pl-result-hint">
              {formatNumber(loans.totalLoans, 1)} loans,{" "}
              {formatMoney(loans.weeklyCost, { cents: true })} per week.
            </span>
          </div>
        </section>
      </div>

      <section className="pl-card pl-card--pad pl-total">
        <div className="pl-total-sum">
          <span>
            <span className="pl-result-label">Developers</span>
            <strong>{formatMoney(dev.totalCost)}</strong>
          </span>
          <span className="pl-total-op" aria-hidden="true">+</span>
          <span>
            <span className="pl-result-label">Loan runs</span>
            <strong>{formatMoney(loans.totalCost)}</strong>
          </span>
          <span className="pl-total-op" aria-hidden="true">=</span>
          <span>
            <span className="pl-result-label">Total pilot cost</span>
            <strong className="pl-total-value">{formatMoney(totalCost)}</strong>
          </span>
        </div>
      </section>
    </div>
  );
}
